import { Cpu, MemoryStick, HardDrive, RotateCw, Trash2, Download, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import type { DockerContainerInfo, InfraSettingRow, OllamaModelInfo } from '@command-room/shared';
import {
  useDbStats,
  useDockerStats,
  useHostStats,
  useInfraSettings,
  useOllamaModels,
  usePullOllamaModel,
  useRemoveOllamaModel,
  useRestartContainer,
  useUpdateInfraSettings,
} from '@/api/queries';
import { StatCardsSkeleton, TableSkeleton } from '@/components/feedback/Skeletons';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectItem } from '@/components/ui/select';
import { bytes, percent } from '@/lib/format';
import { cn } from '@/lib/cn';

export function SystemPage() {
  const { data: host } = useHostStats();
  const { data: db } = useDbStats();
  const { data: containers } = useDockerStats();
  const { data: models } = useOllamaModels();

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">System</h1>
        <p className="text-sm text-muted-foreground">VPS hardware, containers, Postgres growth, and local LLMs.</p>
      </header>

      <section className="grid gap-4 md:grid-cols-3">
        {!host ? (
          <StatCardsSkeleton count={3} />
        ) : (
          <>
            <Metric
              icon={<Cpu className="h-4 w-4" />}
              label="CPU"
              value={percent(host.cpu.usage_percent)}
              sub={`${host.cpu.cores} cores · load ${host.cpu.load_avg.join(' / ')}`}
            />
            <Metric
              icon={<MemoryStick className="h-4 w-4" />}
              label="Memory"
              value={percent(host.mem.used_percent)}
              sub={`${bytes(host.mem.used_bytes)} of ${bytes(host.mem.total_bytes)}`}
              hot={host.mem.used_percent >= 90}
            />
            <Metric
              icon={<HardDrive className="h-4 w-4" />}
              label="Disk"
              value={percent(host.disk.used_percent)}
              sub={`${bytes(host.disk.used_bytes)} of ${bytes(host.disk.total_bytes)} (${host.disk.mount})`}
            />
          </>
        )}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Table sizes</CardTitle>
          {db && db.length > 0 && (
            <CardDescription>
              As of {new Date(Math.max(...db.map((row) => new Date(row.snapshot_at).getTime()))).toLocaleString()}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {!db ? (
            <TableSkeleton widths={['w-48', 'w-20', 'w-20']} rows={5} bordered={false} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Table</th>
                  <th className="px-4 py-2">Rows</th>
                  <th className="px-4 py-2">Size</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {db
                  .slice()
                  .sort((a, b) => b.size_bytes - a.size_bytes)
                  .map((row) => (
                    <tr key={row.table_name}>
                      <td className="px-4 py-2 font-mono text-xs">{row.table_name}</td>
                      <td className="px-4 py-2">{row.row_count.toLocaleString()}</td>
                      <td className="px-4 py-2">{bytes(row.size_bytes)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ContainersCard containers={containers} />

      <OllamaModelsCard models={models} />
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  sub,
  hot,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string | null;
  hot?: boolean;
}) {
  return (
    <Card className={cn(hot && 'border-destructive animate-pulse-ring')}>
      <CardContent className="flex items-start gap-3 p-5">
        <div className={cn('grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary', hot && 'bg-destructive/15 text-destructive')}>
          {icon}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-semibold">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const CONTAINER_STATE_BADGE: Record<string, BadgeProps['variant']> = {
  running: 'success',
  exited: 'destructive',
  dead: 'destructive',
  paused: 'warning',
  restarting: 'warning',
};

function ContainersCard({ containers }: { containers?: DockerContainerInfo[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Containers</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {!containers ? (
          <TableSkeleton widths={['w-32', 'w-40', 'w-20', 'w-14', 'w-20', 'w-16']} rows={4} bordered={false} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Container</th>
                <th className="px-4 py-2">Image</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">CPU</th>
                <th className="px-4 py-2">Memory</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {containers
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <ContainerRow key={c.id} container={c} />
                ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function ContainerRow({ container }: { container: DockerContainerInfo }) {
  const [confirming, setConfirming] = useState(false);
  const restart = useRestartContainer();

  return (
    <tr>
      <td className="px-4 py-2 font-mono text-xs">{container.name}</td>
      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{container.image}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <Badge variant={CONTAINER_STATE_BADGE[container.state] ?? 'muted'}>{container.state}</Badge>
          <span className="text-xs text-muted-foreground">{container.status}</span>
        </div>
      </td>
      <td className="px-4 py-2">{percent(container.cpu_percent, 1)}</td>
      <td className="px-4 py-2">
        {container.mem_usage_bytes != null
          ? `${bytes(container.mem_usage_bytes)} / ${bytes(container.mem_limit_bytes)}`
          : '—'}
      </td>
      <td className="px-4 py-2 text-right">
        {confirming ? (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="destructive"
              size="sm"
              disabled={restart.isPending}
              onClick={() => restart.mutate(container.id, { onSuccess: () => setConfirming(false) })}
            >
              Restart
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="icon" title="Restart" onClick={() => setConfirming(true)}>
            <RotateCw className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
}

const LOCAL_LLM_KEYS = ['n8n_local_default_llm', 'n8n_local_fast_llm', 'n8n_local_thinking_llm'];

function OllamaModelsCard({ models }: { models?: OllamaModelInfo[] }) {
  const [pullName, setPullName] = useState('');
  const pull = usePullOllamaModel();
  const { data: settings } = useInfraSettings();
  const updateSettings = useUpdateInfraSettings();

  function handlePull() {
    const name = pullName.trim();
    if (!name) return;
    pull.mutate(name, { onSuccess: () => setPullName('') });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle>Ollama models</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="h-8 w-52 rounded-md border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            placeholder="model:tag"
            value={pullName}
            onChange={(e) => setPullName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePull()}
            disabled={pull.isPending}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handlePull}
            disabled={pull.isPending || !pullName.trim()}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {pull.isPending ? 'Pulling…' : 'Pull'}
          </Button>
        </div>
      </CardHeader>
      {pull.isError && (
        <div className="mx-4 mb-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {(pull.error as Error).message}
        </div>
      )}
      <CardContent className="p-0">
        {!models ? (
          <TableSkeleton widths={['w-40', 'w-16', 'w-20', 'w-20', 'w-24', 'w-16', 'w-12']} rows={4} bordered={false} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">Size</th>
                <th className="px-4 py-2">Parameters</th>
                <th className="px-4 py-2">Quantization</th>
                <th className="px-4 py-2">Modified</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {models
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((m) => (
                  <OllamaModelRow
                    key={m.name}
                    model={m}
                    allModels={models}
                    settings={settings}
                    saveSettings={(data) => updateSettings.mutateAsync(data)}
                    isSavingSettings={updateSettings.isPending}
                  />
                ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function OllamaModelRow({
  model,
  allModels,
  settings,
  saveSettings,
  isSavingSettings,
}: {
  model: OllamaModelInfo;
  allModels: OllamaModelInfo[];
  settings?: InfraSettingRow[];
  saveSettings: (data: Record<string, string>) => Promise<unknown>;
  isSavingSettings: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const remove = useRemoveOllamaModel();

  const affectedSettings = (settings ?? []).filter(
    (s) => LOCAL_LLM_KEYS.includes(s.key) && s.value === model.name,
  );
  const replacementOptions = allModels.filter((m) => m.name !== model.name).map((m) => m.name);

  function startConfirm() {
    const init: Record<string, string> = {};
    for (const s of affectedSettings) {
      init[s.key] = replacementOptions[0] ?? '';
    }
    setReplacements(init);
    setConfirming(true);
  }

  async function handleDelete() {
    if (affectedSettings.length > 0) {
      await saveSettings(replacements);
    }
    remove.mutate(model.name, { onSuccess: () => setConfirming(false) });
  }

  const canDelete =
    affectedSettings.length === 0 ||
    affectedSettings.every((s) => replacements[s.key]);

  const busy = remove.isPending || isSavingSettings;

  return (
    <>
      <tr>
        <td className="px-4 py-2 font-mono text-xs">{model.name}</td>
        <td className="px-4 py-2">{bytes(model.size_bytes)}</td>
        <td className="px-4 py-2">{model.parameter_size ?? '—'}</td>
        <td className="px-4 py-2">{model.quantization ?? '—'}</td>
        <td className="px-4 py-2 text-xs text-muted-foreground">
          {new Date(model.modified_at).toLocaleString()}
        </td>
        <td className="px-4 py-2">
          {model.loaded ? (
            <Badge variant="success">loaded · {bytes(model.vram_bytes)}</Badge>
          ) : (
            <Badge variant="muted">idle</Badge>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          {confirming && affectedSettings.length === 0 ? (
            <div className="flex items-center justify-end gap-1">
              <Button variant="destructive" size="sm" disabled={busy} onClick={handleDelete}>
                {busy ? 'Deleting…' : 'Delete'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              title="Delete model"
              onClick={startConfirm}
              disabled={confirming}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </td>
      </tr>

      {confirming && affectedSettings.length > 0 && (
        <tr>
          <td colSpan={7} className="px-4 pb-4 pt-0">
            <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
              <p className="mb-3 flex items-center gap-1.5 text-xs font-medium text-warning">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                This model is referenced in{' '}
                {affectedSettings.length === 1
                  ? '1 LLM setting'
                  : `${affectedSettings.length} LLM settings`}
                . Choose a replacement for each before deleting.
              </p>
              <div className="flex flex-col gap-2">
                {affectedSettings.map((s) => (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className="w-44 shrink-0 text-xs text-muted-foreground">
                      {s.label ?? s.key}
                    </span>
                    {replacementOptions.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">
                        No other models installed — setting will be cleared.
                      </span>
                    ) : (
                      <Select
                        value={replacements[s.key] ?? ''}
                        onValueChange={(v) =>
                          setReplacements((r) => ({ ...r, [s.key]: v }))
                        }
                        placeholder="Select replacement…"
                        className="h-7 flex-1 text-xs font-mono"
                      >
                        {replacementOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={busy || !canDelete}
                  onClick={handleDelete}
                >
                  {busy ? 'Saving & deleting…' : 'Confirm deletion'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
